/**
 * Dialog for creating a new translation key.
 *
 * Use case: a developer added a new t('foo.bar') call in code and wants
 * to seed both languages from the admin UI without writing SQL. Most
 * editors won't ever open this — they'll only edit existing keys
 * inline in the table.
 *
 * Validation:
 *   - key is required and must match dot-notation (alpha + dot + digits)
 *   - key must be unique (we pass existingKeys in to check client-side)
 *   - at least one of EN/DE must be filled (avoid creating a totally
 *     empty row that would just confuse the next reader)
 */
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useUpsertTranslation } from '@/hooks/v2/useTranslations'
import { useLanguage } from '@/contexts/LanguageContext'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Used for client-side duplicate-key detection. */
  existingKeys: string[]
  onCreated: () => void
}

// Dot-notation: lowercase letters/digits, separated by dots, no leading/trailing dot.
// e.g. "property.amenity.wifi" ✓, "Property.Wifi" ✗, ".foo" ✗.
const KEY_SHAPE = /^[a-z0-9]+(\.[a-z0-9]+)*$/

export function AddTranslationDialog({
  open,
  onOpenChange,
  existingKeys,
  onCreated,
}: Props) {
  const [key, setKey] = useState('')
  const [en, setEn] = useState('')
  const [de, setDe] = useState('')
  const [description, setDescription] = useState('')
  const upsertMut = useUpsertTranslation()
  const { toast } = useToast()
  const { t } = useLanguage()

  function reset() {
    setKey('')
    setEn('')
    setDe('')
    setDescription('')
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset()
    onOpenChange(next)
  }

  const trimmedKey = key.trim()
  const keyError = (() => {
    if (!trimmedKey) return null // don't show error before user types
    if (!KEY_SHAPE.test(trimmedKey)) {
      return 'Use dot-notation (e.g. "property.amenity.wifi"). Lowercase letters/digits and dots only.'
    }
    if (existingKeys.includes(trimmedKey)) {
      return 'Key already exists — edit it inline in the table instead.'
    }
    return null
  })()

  const canSubmit =
    !!trimmedKey &&
    !keyError &&
    (en.trim().length > 0 || de.trim().length > 0) &&
    !upsertMut.isPending

  async function handleSubmit() {
    if (!canSubmit) return
    try {
      await upsertMut.mutateAsync({
        key: trimmedKey,
        en: en.trim() || null,
        de: de.trim() || null,
        description: description.trim() || null,
      })
      reset()
      onCreated()
    } catch (err) {
      toast({
        title: 'Could not add key',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('admin.translations.addKey', 'Add translation key')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'admin.translations.addKeyDesc',
              'Create a new dot-notation key. You can fill one or both languages now and edit later.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              {t('admin.translations.key', 'Key')}
            </Label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase())}
              placeholder="property.amenity.balcony"
              className="font-mono text-sm"
              autoFocus
            />
            {keyError && (
              <p className="text-xs text-destructive">{keyError}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                {t('admin.translations.english', 'English')}
              </Label>
              <Input
                value={en}
                onChange={(e) => setEn(e.target.value)}
                placeholder="Balcony"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                {t('admin.translations.german', 'German')}
              </Label>
              <Input
                value={de}
                onChange={(e) => setDe(e.target.value)}
                placeholder="Balkon"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">
              {t('admin.description', 'Description')}{' '}
              <span className="text-muted-foreground font-normal">
                ({t('admin.field.noteOptional', 'optional')})
              </span>
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder={t(
                'admin.translations.translatorNoteHint',
                'Where this string appears, special handling for translators…',
              )}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={upsertMut.isPending}
          >
            {t('admin.action.cancel', 'Cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {upsertMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('admin.translations.adding', 'Adding…')}
              </>
            ) : (
              t('admin.action.addKey', 'Add key')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
